import { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { useCreateJobPosting, useJobPosting, useUpdateJobPosting } from "@/hooks/useMyJobs";
import { useJobCredits, useDecrementCredits } from "@/hooks/useJobCredits";
import { JobFormStep1 } from "@/components/jobs/form/JobFormStep1";
import { JobFormStep2 } from "@/components/jobs/form/JobFormStep2";
import { JobFormStep3 } from "@/components/jobs/form/JobFormStep3";
import { JobFormStep4 } from "@/components/jobs/form/JobFormStep4";
import { JobFormStep5 } from "@/components/jobs/form/JobFormStep5";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { ArrowLeft, ArrowRight, Send, Loader2, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ContractType, WorkMode, SalaryPeriod, ApplicationMethod, InclusivityTag } from "@/data/jobOptions";
import { sanitizeText, sanitizeRichText, sanitizeEmail, sanitizeUrl } from "@/lib/sanitize";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export interface JobFormData {
  title: string;
  location_city: string;
  location_region: string;
  contract_type: ContractType;
  work_mode: WorkMode;
  salary_range_min: number | null;
  salary_range_max: number | null;
  salary_period: SalaryPeriod | null;
  description: string; // Legacy field - kept for backward compatibility
  description_it: string;
  description_en: string;
  description_es: string;
  required_skills: string[];
  nice_to_have_skills: string[];
  inclusivity_tags: InclusivityTag[];
  inclusive_benefits: string[];
  application_method: ApplicationMethod;
  application_email: string;
  application_url: string;
}

const initialFormData: JobFormData = {
  title: '',
  location_city: '',
  location_region: '',
  contract_type: 'determinato',
  work_mode: 'presenza',
  salary_range_min: null,
  salary_range_max: null,
  salary_period: null,
  description: '',
  description_it: '',
  description_en: '',
  description_es: '',
  required_skills: [],
  nice_to_have_skills: [],
  inclusivity_tags: [],
  inclusive_benefits: [],
  application_method: 'upload_cv',
  application_email: '',
  application_url: '',
};

const STEPS_CONFIG = [
  { id: 1, labelKey: 'jobs.form.step1Title', descriptionKey: 'jobs.form.step1Desc' },
  { id: 2, labelKey: 'jobs.form.step2Title', descriptionKey: 'jobs.form.step2Desc' },
  { id: 3, labelKey: 'jobs.form.step3Title', descriptionKey: 'jobs.form.step3Desc' },
  { id: 4, labelKey: 'jobs.form.step4Title', descriptionKey: 'jobs.form.step4Desc' },
  { id: 5, labelKey: 'jobs.form.step5Title', descriptionKey: 'jobs.form.step5Desc' },
];

const JobCreate = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();
  const isEditMode = !!id;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<JobFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: credits } = useJobCredits();
  const { data: existingJob, isLoading: loadingJob } = useJobPosting(id);
  const createJob = useCreateJobPosting();
  const updateJob = useUpdateJobPosting();
  const decrementCredits = useDecrementCredits();

  // Populate form with existing job data in edit mode
  useEffect(() => {
    if (existingJob && isEditMode) {
      // Type assertion to handle new fields that may not be in types yet
      const job = existingJob as typeof existingJob & {
        description_it?: string;
        description_en?: string;
        description_es?: string;
      };
      setFormData({
        title: job.title || '',
        location_city: job.location_city || '',
        location_region: job.location_region || '',
        contract_type: job.contract_type,
        work_mode: job.work_mode,
        salary_range_min: job.salary_range_min,
        salary_range_max: job.salary_range_max,
        salary_period: job.salary_period,
        description: job.description || '',
        description_it: job.description_it || job.description || '',
        description_en: job.description_en || '',
        description_es: job.description_es || '',
        required_skills: job.required_skills || [],
        nice_to_have_skills: job.nice_to_have_skills || [],
        inclusivity_tags: (job.inclusivity_tags || []) as InclusivityTag[],
        inclusive_benefits: job.inclusive_benefits || [],
        application_method: job.application_method,
        application_email: job.application_email || '',
        application_url: job.application_url || '',
      });
    }
  }, [existingJob, isEditMode]);

  // Handle job not found in edit mode
  useEffect(() => {
    if (isEditMode && !loadingJob && !existingJob && id) {
      toast({
        title: t("common.error"),
        description: t("jobs.edit.notFound"),
        variant: "error",
      });
      navigate("/dashboard/company/jobs");
    }
  }, [isEditMode, loadingJob, existingJob, id, toast, t, navigate]);

  if (!isLoading && !isAuthenticated) {
    navigate("/auth/login");
    return null;
  }

  // Only redirect for no credits in create mode, not edit mode
  if (!isEditMode && credits && credits.credits_available === 0) {
    navigate("/dashboard/company/jobs");
    return null;
  }

  // Show loading while fetching existing job in edit mode
  if (isEditMode && loadingJob) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  const updateFormData = (updates: Partial<JobFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (status: 'active' | 'draft' = 'active') => {
    if (!credits?.company_id) return;

    setIsSubmitting(true);
    
    // Sanitize all form fields before submission
    const sanitizedFormData = {
      ...formData,
      title: sanitizeText(formData.title),
      location_city: sanitizeText(formData.location_city),
      location_region: sanitizeText(formData.location_region),
      description: sanitizeRichText(formData.description),
      description_it: sanitizeRichText(formData.description_it),
      description_en: sanitizeRichText(formData.description_en),
      description_es: sanitizeRichText(formData.description_es),
      required_skills: formData.required_skills.map(s => sanitizeText(s)),
      nice_to_have_skills: formData.nice_to_have_skills.map(s => sanitizeText(s)),
      inclusive_benefits: formData.inclusive_benefits.map(b => sanitizeText(b)),
      application_email: sanitizeEmail(formData.application_email),
      application_url: sanitizeUrl(formData.application_url),
    };
    
    try {
      if (isEditMode && id) {
        // UPDATE existing job
        await updateJob.mutateAsync({
          id,
          ...sanitizedFormData,
        });
        toast({
          title: t("jobs.edit.success"),
          description: t("jobs.edit.successDesc"),
        });
      } else {
        // CREATE new job
        await createJob.mutateAsync({
          ...sanitizedFormData,
          company_id: credits.company_id,
          status: status,
        });
        
        // Decrement credits only when publishing, not for drafts
        if (status === 'active') {
          await decrementCredits.mutateAsync(credits.company_id);
          toast({
            title: t("jobs.create.success"),
            description: t("jobs.create.successDesc"),
          });
        } else {
          toast({
            title: t("jobs.create.draftSuccess"),
            description: t("jobs.create.draftSuccessDesc"),
          });
        }
      }

      navigate("/dashboard/company/jobs");
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || (isEditMode ? t("jobs.edit.error") : t("jobs.create.error")),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <JobFormStep1 formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <JobFormStep2 formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <JobFormStep3 formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <JobFormStep4 formData={formData} updateFormData={updateFormData} />;
      case 5:
        return <JobFormStep5 formData={formData} />;
      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.title && formData.location_city && formData.contract_type && formData.work_mode;
      case 2:
        // Italian description is required, min 100 chars
        return formData.description_it && formData.description_it.length >= 100;
      case 3:
        return formData.inclusivity_tags.length > 0;
      case 4:
        if (formData.application_method === 'email') return formData.application_email;
        if (formData.application_method === 'external_link') return formData.application_url;
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <SEO 
        title={(isEditMode ? t("jobs.edit.title") : t("jobs.create.title")) + " | Inclusia"} 
        description={isEditMode ? t("jobs.edit.subtitle") : t("jobs.create.subtitle")}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">{t("common.dashboard")}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard/company/jobs">{t("jobs.manage.title")}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{isEditMode ? t("jobs.edit.title") : t("jobs.create.title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header with back arrow + title */}
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate("/dashboard/company/jobs")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading text-3xl font-bold text-dark">
              {isEditMode ? t("jobs.edit.title") : t("jobs.create.title")}
            </h1>
          </div>

          {/* Aligned subtitle */}
          <p className="text-muted-foreground mb-8 ml-14">
            {isEditMode ? t("jobs.edit.subtitle") : t("jobs.create.subtitle")}
          </p>

          {/* Current Step Header */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-heading font-bold text-foreground uppercase tracking-wide">
              {t(STEPS_CONFIG[currentStep - 1].labelKey)}
            </h2>
            <p className="text-muted-foreground font-body text-sm mt-1">
              {t(STEPS_CONFIG[currentStep - 1].descriptionKey)}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-start mb-8 px-2">
            {STEPS_CONFIG.map((s, index) => {
              const isCompleted = currentStep > s.id;
              const isActive = currentStep === s.id;
              
              return (
                <div key={s.id} className={`flex flex-col items-start ${index < STEPS_CONFIG.length - 1 ? 'flex-1' : ''}`}>
                  <div className="flex items-center w-full">
                    {/* Step Circle */}
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 border-[3px] border-dark flex items-center justify-center shadow-[3px_3px_0px_0px] shadow-dark transition-colors
                      ${isCompleted ? 'bg-success text-success-foreground' : isActive ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground'}`}>
                      {isCompleted ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <span className="font-heading font-bold text-lg sm:text-xl">{s.id}</span>}
                    </div>
                    {/* Connector Line */}
                    {index < STEPS_CONFIG.length - 1 && (
                      <div className={`flex-1 h-1 border-t-[3px] mx-1 sm:mx-2 transition-colors
                        ${currentStep > s.id ? 'border-success' : 'border-dark/30'}`} />
                    )}
                  </div>
                  {/* Step Label */}
                  <span className={`text-[9px] sm:text-xs font-heading mt-2 text-left uppercase tracking-wide leading-tight w-full pr-2
                    ${isActive ? 'text-primary font-bold' : isCompleted ? 'text-success font-medium' : 'text-muted-foreground'}`}>
                    {t(s.labelKey)}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="bg-card border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 md:p-8">
            {renderStep()}
          </div>

          <div className="flex justify-between mt-6">
            <BrutalButton
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("common.back")}
            </BrutalButton>

            {currentStep < 5 ? (
              <BrutalButton
                onClick={handleNext}
                disabled={!isStepValid()}
              >
                {t("common.next")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </BrutalButton>
            ) : (
              <div className="flex gap-3">
                {/* Show "Save Draft" only in create mode, not edit mode */}
                {!isEditMode && (
                  <BrutalButton
                    variant="secondary"
                    onClick={() => handleSubmit('draft')}
                    disabled={isSubmitting}
                  >
                    {t("jobs.create.saveDraft")}
                  </BrutalButton>
                )}
                <BrutalButton
                  onClick={() => handleSubmit('active')}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("common.submitting") : (isEditMode ? t("jobs.edit.saveChanges") : t("jobs.create.publish"))}
                  <Send className="w-4 h-4 ml-2" />
                </BrutalButton>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default JobCreate;
